const safeParse = (key, fallback) => {
    try {
        const raw = localStorage.getItem(key);
        if (raw === null || raw === undefined) return fallback;
        const parsed = JSON.parse(raw);
        return parsed ?? fallback;
    } catch {
        return fallback;
    }
};

// ---------------------------------------------------------------------------
// In-memory cache — avoids re-parsing JSON on every call (e.g. AG Grid cell
// renderers can call canViewField thousands of times per render).
// Invalidated on login/logout via clearPermissionCache().
// ---------------------------------------------------------------------------
let _cache = {
    permissions: null,        // string[]
    hiddenFields: null,       // string[] (lowercased)
    details: null,            // rule objects
};

export const clearPermissionCache = () => {
    _cache = { permissions: null, hiddenFields: null, details: null };
};

// Auto-invalidate on logout (App.js already dispatches this event)
if (typeof window !== 'undefined') {
    window.addEventListener('userLoggedOut', clearPermissionCache);
    // Also invalidate if another tab changes auth/permission data
    window.addEventListener('storage', (e) => {
        if (
            !e.key || // storage.clear()
            ['permissions', 'hiddenFields', 'fieldPermissionDetails'].includes(e.key)
        ) {
            clearPermissionCache();
        }
    });
}

const getPermissions = () => {
    if (_cache.permissions === null) {
        _cache.permissions = safeParse('permissions', []);
    }
    return _cache.permissions;
};

const getHiddenFieldsCached = () => {
    if (_cache.hiddenFields === null) {
        _cache.hiddenFields = safeParse('hiddenFields', []).map((f) =>
            String(f).toLowerCase()
        );
    }
    return _cache.hiddenFields;
};

const getDetails = () => {
    if (_cache.details === null) {
        _cache.details = safeParse('fieldPermissionDetails', []);
    }
    return _cache.details;
};

// ---------------------------------------------------------------------------
// Module-level permission checks
// ---------------------------------------------------------------------------

// Check if user has a specific module/action permission, e.g. 'Reports.Export'
export const hasPermission = (permission) => {
    return getPermissions().includes(permission);
};

// Check if user has ANY of the given permissions (OR condition)
export const hasAnyPermission = (permissionsArray = []) => {
    return permissionsArray.some((perm) => hasPermission(perm));
};

// Check if user has ALL of the given permissions (AND condition)
export const hasAllPermissions = (permissionsArray = []) => {
    return permissionsArray.every((perm) => hasPermission(perm));
};

// ---------------------------------------------------------------------------
// User identity helpers
// ---------------------------------------------------------------------------

// Get current logged-in user's role/type (stored during login)
export const getUserRole = () => {
    return localStorage.getItem('userTypeName') || '';
};

export const getUserTypeID = () => {
    const id = localStorage.getItem('userTypeID');
    return id ? Number(id) : null;
};

// Restricted external roles → field rules must FAIL CLOSED for them
// (2 = Client, 3 = Consultant per API response; extend if other external types exist)
const RESTRICTED_USER_TYPE_IDS = [2, 3];
const CLIENT_USER_TYPE_ID = 2;
const CONSULTANT_USER_TYPE_ID = 3;

export const isRestrictedUser = () => {
    return RESTRICTED_USER_TYPE_IDS.includes(getUserTypeID());
};

// ✅ NEW: Consultant-specific check (distinct from Client, both of which
// isRestrictedUser() lumps together). Falls back to matching the role
// name string in case userTypeID isn't populated for some sessions.
export const isConsultant = () => {
    const typeId = getUserTypeID();
    if (typeId !== null) {
        return typeId === CONSULTANT_USER_TYPE_ID;
    }
    return getUserRole().trim().toLowerCase() === 'consultant';
};

// ✅ NEW: Client-specific check, for symmetry/clarity anywhere you need to
// distinguish Client from Consultant instead of using isRestrictedUser().
export const isClient = () => {
    const typeId = getUserTypeID();
    if (typeId !== null) {
        return typeId === CLIENT_USER_TYPE_ID;
    }
    return getUserRole().trim().toLowerCase() === 'client';
};

export const isListAgent = () => {
    const role = getUserRole().trim().toLowerCase();
    return role === 'list agency' || role === 'list agent';
};

// Check if user is authenticated at all (token exists).
// NOTE: this only checks token PRESENCE, not validity/expiry — the API layer
// must still handle 401s and force logout.
export const isAuthenticated = () => {
    const authToken = localStorage.getItem('authToken');
    const userToken = localStorage.getItem('userToken');
    const sessionToken = sessionStorage.getItem('userSession');
    return !!(authToken || userToken || sessionToken);
};

// ---------------------------------------------------------------------------
// Field-level RBAC
// ---------------------------------------------------------------------------

// Raw hidden fields list (lowercased)
export const getHiddenFields = () => {
    return getHiddenFieldsCached();
};

// Whether field permission data has been loaded at all
const hasFieldRulesLoaded = () => {
    return (
        localStorage.getItem('hiddenFields') !== null ||
        localStorage.getItem('fieldPermissionDetails') !== null
    );
};

export const canViewField = (fieldName) => {
    if (!fieldName) return true;

    if (!hasFieldRulesLoaded()) {
        // No rules present: fail closed for restricted users, open for internal
        return !isRestrictedUser();
    }

    return !getHiddenFieldsCached().includes(String(fieldName).toLowerCase());
};

export const canViewFieldOnScreen = (module, screenName, fieldName) => {
    if (!fieldName) return true;

    if (!hasFieldRulesLoaded()) {
        return !isRestrictedUser();
    }

    const rule = getDetails().find(
        (r) =>
            r.module === module &&
            r.screenName === screenName &&
            String(r.fieldName).toLowerCase() === String(fieldName).toLowerCase()
    );

    return rule ? !!rule.canView : true;
};

export const canEditFieldOnScreen = (module, screenName, fieldName) => {
    if (!fieldName) return true;

    if (!hasFieldRulesLoaded()) {
        return !isRestrictedUser();
    }

    const rule = getDetails().find(
        (r) =>
            r.module === module &&
            r.screenName === screenName &&
            String(r.fieldName).toLowerCase() === String(fieldName).toLowerCase()
    );

    return rule ? !!rule.canEdit : true;
};

// Bulk helper: filter AG Grid / table column defs by screen rules.
// filterColumnsByScreen(colDefs, 'Jobs', 'JobList', (c) => c.field)
export const filterColumnsByScreen = (columns, module, screenName, getFieldName) => {
    return columns.filter((col) =>
        canViewFieldOnScreen(module, screenName, getFieldName(col))
    );
};

// ---------------------------------------------------------------------------
// Post-login persistence + routing
// ---------------------------------------------------------------------------

// Call this ONCE right after login / OTP verify with the full API response.
// Guarantees all keys the helpers above depend on are seeded consistently.
export const storePermissionsFromApi = (apiResponse) => {
    try {
        localStorage.setItem('userTypeID', String(apiResponse.userTypeID ?? ''));
        localStorage.setItem('userTypeName', apiResponse.userTypeName ?? '');
        localStorage.setItem(
            'permissions',
            JSON.stringify(apiResponse.modulePermissions?.permissions || [])
        );
        localStorage.setItem(
            'hiddenFields',
            JSON.stringify(apiResponse.fieldPermissions?.hiddenFields || [])
        );
        localStorage.setItem(
            'fieldPermissionDetails',
            JSON.stringify(apiResponse.fieldPermissions?.details || [])
        );
    } finally {
        clearPermissionCache(); // force fresh reads after (re)login
    }
};

// Decide which route to land on after login, based on sidebar order + permissions.
// Order matches Sidebar.js menuItems: client -> reports -> tallies -> maintainance -> JobsModule
export const getDefaultRoute = () => {
     if (isListAgent()) {
        return hasPermission('Job.View') ? '/job-module' : '/unauthorized';
    }

    
    if (hasPermission('ClientPortal.View')) {
        return '/dashboard';
    }
    if (hasPermission('Reports.View')) {
        return '/reports';
    }
    if (hasAnyPermission(['Tallies.View', 'LoL.View'])) {
        return '/talliesSection';
    }
    if (hasPermission('Maintenance.View')) {
        return '/maintenance';
    }
    if (hasPermission('Job.View')) {
        return '/job-module';
    }

    // No module permission matched -> user has no visible menu item.
    // Fall back to /unauthorized rather than dumping them on a blank dashboard.
    return '/unauthorized';
};